import React from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Stack,
  Typography
} from "@mui/material";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  getBadgeSettings,
  IBadgeSettings,
  TIER_CONFIG,
  LEVEL_CONFIG
} from "network/apis/badges";
import { QUERIES } from "utils/constants";
import { PATH_NAME } from "routes/pathNames";

interface BadgeInfoDialogProps {
  open: boolean;
  onClose: () => void;
}

const tierThresholds: { key: string; field: keyof IBadgeSettings | null }[] = [
  { key: "starter", field: null },
  { key: "contributor", field: "starterThreshold" },
  { key: "achiever", field: "contributorThreshold" },
  { key: "champion", field: "achieverThreshold" }
];

const levelThresholds: { level: number; field: keyof IBadgeSettings | null }[] =
  [
    { level: 1, field: null },
    { level: 2, field: "level1Threshold" },
    { level: 3, field: "level2Threshold" },
    { level: 4, field: "level3Threshold" },
    { level: 5, field: "level4Threshold" }
  ];

const BadgeInfoDialog: React.FC<BadgeInfoDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { data: settings, isLoading } = useQuery(
    QUERIES.LOAD_BADGE_SETTINGS,
    getBadgeSettings,
    { enabled: open }
  );

  const handleLearnMore = () => {
    onClose();
    navigate(PATH_NAME.HELP_CENTER + "#badges");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 1, overflow: "hidden" }
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
          py: 2.5,
          px: 3
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          How Badges & Levels Work
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: "#6B7280", mb: 2 }}>
              Every time someone gives you kudos, you earn points. Points count
              toward both your quarterly badge tier and your lifetime level.
            </Typography>

            {/* Quarterly Badge Tiers */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Quarterly Badge Tiers
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
              Badge tiers reset at the start of each calendar quarter.
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {tierThresholds.map(({ key, field }) => {
                const config = TIER_CONFIG[key];
                const threshold = field ? settings?.[field] ?? "\u2014" : 0;
                return (
                  <Stack
                    key={key}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    <Chip
                      label={`${config.icon} ${config.label}`}
                      size="small"
                      sx={{
                        bgcolor: config.bgColor,
                        color: config.color,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: "24px",
                        border: `1px solid ${config.color}20`,
                        minWidth: 110
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {threshold}+ points
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Lifetime Levels */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Lifetime Levels
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
              Lifetime levels are permanent and never reset.
            </Typography>
            <Stack spacing={1}>
              {levelThresholds.map(({ level, field }) => {
                const config = LEVEL_CONFIG[level];
                const threshold = field ? settings?.[field] ?? "\u2014" : 0;
                return (
                  <Stack
                    key={level}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    <Chip
                      label={`${config.icon} Level ${level}`}
                      size="small"
                      sx={{
                        bgcolor: "#F3F4F6",
                        color: config.color,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: "24px",
                        border: `1px solid ${config.color}20`,
                        minWidth: 110
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {threshold}+ points
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.grey[50]
        }}
      >
        <MuiLink
          component="button"
          variant="body2"
          onClick={handleLearnMore}
          sx={{ textDecoration: "underline", cursor: "pointer" }}
        >
          Learn more in Help Center
        </MuiLink>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          disableElevation
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeInfoDialog;
